import type { ReactNode } from "react";
import { useCallback, useMemo, useState } from "react";

import styled, { createGlobalStyle } from "styled-components";

import type { ColumnsType } from "@similarweb/pro-logic-components";
import { CSS_TABLE_CELL_HOVER_CLASS_NAME, Table } from "@similarweb/pro-logic-components";
import { colorsPalettes } from "@similarweb/styles";
import { Masonry } from "@similarweb/ui-components";
import { Box } from "@similarweb/ui-components/foundations";

import { GenericEmptyState } from "app/components/React/empty-state/GenericEmptyState";
import { usePagination } from "app/custom-hooks/usePagination";
import { translate } from "app/filters/i18nFilter";
import { numberFilter } from "app/filters/numberFilter";
import { AdModal } from "app/pages/digital-marketing/ad-intelligence/common/ads/modal/AdModal";
import type { AdIntelligenceController } from "app/pages/digital-marketing/ad-intelligence/common/shared/types/types";
import { getNoDataObject } from "app/pages/digital-marketing/ad-intelligence/common/utils/utils";
// Pre-existing dependency relocated from DisplayAds when this presentational component was extracted.
// fallow-ignore-next-line boundary-violation
import { SwitcherGranularityContainer } from "app/pages/website-analysis/components/SwitcherGranularityContainer";
import { allTrackers } from "app/services/track/track";

import { AdIntelligenceAdCard } from "./AdIntelligenceAdCard";
import { getLoadingCards } from "./AdIntelligenceAdCardLoading";
import { getColumns, ShowModalButtonWrapper } from "./AdIntelligenceAdsColumnConfig";
import type { ISortOption } from "./adsSortOptions";
import { AdsSortSelect } from "./AdsSortSelect";
import { useAdIntelligenceAdsViewSwitcher, ViewType } from "./hooks/useAdIntelligenceAdsViewSwitcher";
import type { AdsGalleryData } from "./hooks/useAdsGallery";
import { MixedScrapperBanner } from "./MixedScrapperBanner";
import {
    EmptyStateWrapper,
    Footer,
    MasonryContainer,
    Title,
    TitleRow,
    TitleRowActions,
    TitleRowInner,
} from "./StyledComponents";

const MASONRY_GUTTER = 12;
const NUMBER_OF_ADS_PER_PAGE = 100;
const NOOP = () => {};

const AdCardImageGlobalStyles = createGlobalStyle`
    ${CSS_TABLE_CELL_HOVER_CLASS_NAME} ${ShowModalButtonWrapper} {
        opacity: 1;
    }
`;

const TableContainer = styled.div`
    border-top: 1px solid ${colorsPalettes.carbon[50]};
`;

const SwitcherGranularityContainerStyled = styled(SwitcherGranularityContainer)`
    margin-right: 0;
`;

const columnsCountBreakPoints = {
    0: 2,
    904: 3,
};

interface IAdsGalleryProps {
    data: AdsGalleryData[];
    isLoading: boolean;
    isError: boolean;
    totalCount: number;
    controller: AdIntelligenceController;
    industryName?: string;
    sortOptions: ISortOption[];
    selectedSort: string;
    onSortChange: (id: string) => void;
    currentPage: number;
    onPageChange: (page: number) => void;
    titleLabelKey: string;
    scraperBannerDate?: string;
    filtersSlot?: ReactNode;
    compareTabsSlot?: ReactNode;
    downloadSlot?: ReactNode;
    showAdvertiserColumn?: boolean;
    // When provided, replaces the pagination controls in the footer (e.g. a
    // "See all" button in overview context that links to the full report).
    footerSlot?: ReactNode;
    // Notice rendered full-bleed between the title row and the gallery; stays
    // visible when the gallery falls back to its empty state.
    noticeSlot?: ReactNode;
}

export const AdsGallery = ({
    data,
    isLoading,
    isError,
    totalCount,
    controller,
    industryName,
    sortOptions,
    selectedSort,
    onSortChange,
    currentPage,
    onPageChange,
    titleLabelKey,
    scraperBannerDate,
    filtersSlot,
    compareTabsSlot,
    downloadSlot,
    showAdvertiserColumn = false,
    footerSlot,
    noticeSlot,
}: IAdsGalleryProps) => {
    const [currentSelectedAdIndex, setCurrentSelectedAdIndex] = useState<number | null>(null);

    const { config: viewTypeSwitcherConfig, currentView } = useAdIntelligenceAdsViewSwitcher(isLoading);

    // eslint-disable-next-line @typescript-eslint/naming-convention
    const [_, paginationComponent] = usePagination({
        listItemsLength: totalCount ?? 0,
        initialPage: currentPage,
        itemsPerPage: NUMBER_OF_ADS_PER_PAGE,
        callbackOnPageChange: (page: number) => {
            onPageChange(page);
            const pageElement = document.getElementsByClassName("sw-layout-scrollable-element")?.[0];
            pageElement?.scroll?.({ top: 0, behavior: "smooth" });
        },
    });

    const handleCardClick = useCallback(
        (index: number) => {
            setCurrentSelectedAdIndex(index);
            allTrackers.dispatchTrackEvent("Pop Up", "open", `Ads/More info/${data[index]?.creativeType}`);
        },
        [data],
    );

    const ads = useMemo(
        () =>
            isLoading
                ? getLoadingCards()
                : data.map((item, index) => (
                      <AdIntelligenceAdCard
                          key={item.creativeId}
                          onClick={() => handleCardClick(index)}
                          {...item}
                      />
                  )),
        [isLoading, data, handleCardClick],
    );

    const columns = useMemo(
        () =>
            getColumns(
                currentPage,
                NUMBER_OF_ADS_PER_PAGE,
                handleCardClick,
                controller,
                false,
                showAdvertiserColumn,
            ) as ColumnsType,
        [currentPage, controller, handleCardClick, showAdvertiserColumn],
    );

    const noDataObject = getNoDataObject(isError);

    return (
        <>
            {scraperBannerDate !== undefined && <MixedScrapperBanner newScrapingDateString={scraperBannerDate} />}
            <Box
                data-automation="display-ads-gallery"
                width="100%"
                height="auto"
                background={colorsPalettes.carbon[0]}
                borderRadius="6px"
            >
                {compareTabsSlot ?? null}
                <TitleRow>
                    <TitleRowInner>
                        <Title>
                            {numberFilter()(totalCount)} {translate(titleLabelKey)}
                        </Title>
                        {filtersSlot ?? null}
                    </TitleRowInner>
                    <TitleRowActions>
                        <SwitcherGranularityContainerStyled
                            itemList={viewTypeSwitcherConfig.items}
                            selectedIndex={viewTypeSwitcherConfig.initialSelectedItem}
                            onItemClick={viewTypeSwitcherConfig.onItemClick ?? NOOP}
                            customClass="CircleSwitcher"
                            className="swichers-container"
                        />
                        <AdsSortSelect
                            sortOptions={sortOptions}
                            selectedSort={selectedSort}
                            onSortChange={onSortChange}
                        />
                        {downloadSlot ?? null}
                    </TitleRowActions>
                </TitleRow>
                {noticeSlot}
                {!isLoading && !ads.length ? (
                    <EmptyStateWrapper>
                        <GenericEmptyState
                            isFullPage={false}
                            title={noDataObject?.title}
                            subtitle={noDataObject?.subtitle}
                        />
                    </EmptyStateWrapper>
                ) : currentView === ViewType.Gallery ? (
                    <MasonryContainer>
                        <Masonry
                            breakpoints={columnsCountBreakPoints}
                            gutter={MASONRY_GUTTER}
                        >
                            {...ads}
                        </Masonry>
                    </MasonryContainer>
                ) : (
                    <TableContainer>
                        <Table
                            data={data}
                            isLoading={isLoading}
                            loaderRowCount={13}
                            size="large"
                            columnConfig={columns}
                            pagination={{ pageSize: NUMBER_OF_ADS_PER_PAGE }}
                        />
                    </TableContainer>
                )}
                {totalCount > 0 ? (
                    <Footer $showBorderTop={currentView === ViewType.Gallery}>
                        {footerSlot ?? paginationComponent}
                    </Footer>
                ) : null}
                {currentSelectedAdIndex !== null ? (
                    <AdModal
                        isOpen={true}
                        data={data}
                        currentSelectedAdIndex={currentSelectedAdIndex}
                        onClose={() => setCurrentSelectedAdIndex(null)}
                        onSelectedAdIndexChange={setCurrentSelectedAdIndex}
                        controller={controller}
                        industryName={industryName}
                    />
                ) : null}
                <AdCardImageGlobalStyles />
            </Box>
        </>
    );
};
